import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminCronHealth } from '@/hooks/admin/useAdminCronHealth';
import { useAdminAdapterHealth } from '@/hooks/admin/useAdminAdapterHealth';
import { useAdminFleetPulse } from '@/hooks/admin/useAdminFleetPulse';
import { useAdminForecastAccuracy } from '@/hooks/admin/useAdminForecastAccuracy';
import { useAdminAutomationHealth } from '@/hooks/admin/useAdminAutomationHealth';
import { useAdminTelemetryHealth } from '@/hooks/admin/useAdminTelemetryHealth';
import { useAdminJobRuns } from '@/hooks/admin/useAdminJobRuns';
import { useAdminOnboardingChecklist } from '@/hooks/admin/useAdminOnboardingChecklist';
import { useAdminPendingUsers, useAdminAllUsers } from '@/hooks/admin/useAdminPendingUsers';
import { CronHealthTab } from '@/components/admin/CronHealthTab';
import { AdapterHealthTab } from '@/components/admin/AdapterHealthTab';
import { FleetPulseTab } from '@/components/admin/FleetPulseTab';
import { ForecastAccuracyTab } from '@/components/admin/ForecastAccuracyTab';
import { AutomationHealthTab } from '@/components/admin/AutomationHealthTab';
import { UserApprovalTab } from '@/components/admin/UserApprovalTab';
import { BatteryTopologyTab } from '@/components/admin/BatteryTopologyTab';
import { ReportReadinessTab } from '@/components/admin/ReportReadinessTab';
import { TelemetryHealthTab } from '@/components/admin/TelemetryHealthTab';
import { NotificationEligibilityTab } from '@/components/admin/NotificationEligibilityTab';
import { JobRunsTab } from '@/components/admin/JobRunsTab';
import { OnboardingChecklistTab } from '@/components/admin/OnboardingChecklistTab';

const REFRESH_INTERVAL = 60_000;

type AdminTab = 'users' | 'cron' | 'adapters' | 'fleet' | 'forecast' | 'automations' | 'topology' | 'reports' | 'telemetry' | 'jobs' | 'onboarding' | 'notif_eligibility';

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [forecastOrgId, setForecastOrgId] = useState<string | null>(null);

  const interval = autoRefresh ? REFRESH_INTERVAL : false;

  const pendingUsers = useAdminPendingUsers(activeTab === 'users', interval);
  const allUsers = useAdminAllUsers(activeTab === 'users', interval);
  const cronHealth = useAdminCronHealth(activeTab === 'cron', interval);
  const adapterHealth = useAdminAdapterHealth(activeTab === 'adapters', interval);
  const fleetPulse = useAdminFleetPulse(activeTab === 'fleet', interval);
  const forecastAccuracy = useAdminForecastAccuracy(activeTab === 'forecast', interval, lookbackDays, forecastOrgId);
  const automationHealth = useAdminAutomationHealth(activeTab === 'automations', interval);
  const telemetryHealth = useAdminTelemetryHealth(activeTab === 'telemetry', interval);
  const jobRuns = useAdminJobRuns(activeTab === 'jobs', interval);
  const onboardingChecklist = useAdminOnboardingChecklist(activeTab === 'onboarding', interval);

  // Track "last refreshed" counter
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastRefreshedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [lastRefreshedAt]);

  // Update lastRefreshedAt when any query succeeds
  useEffect(() => {
    if (pendingUsers.dataUpdatedAt || cronHealth.dataUpdatedAt || adapterHealth.dataUpdatedAt ||
        fleetPulse.dataUpdatedAt || forecastAccuracy.dataUpdatedAt || automationHealth.dataUpdatedAt ||
        telemetryHealth.dataUpdatedAt || jobRuns.dataUpdatedAt || onboardingChecklist.dataUpdatedAt) {
      const latest = Math.max(
        pendingUsers.dataUpdatedAt ?? 0,
        cronHealth.dataUpdatedAt ?? 0,
        adapterHealth.dataUpdatedAt ?? 0,
        fleetPulse.dataUpdatedAt ?? 0,
        forecastAccuracy.dataUpdatedAt ?? 0,
        automationHealth.dataUpdatedAt ?? 0,
        telemetryHealth.dataUpdatedAt ?? 0,
        jobRuns.dataUpdatedAt ?? 0,
        onboardingChecklist.dataUpdatedAt ?? 0
      );
      if (latest > 0) setLastRefreshedAt(latest);
    }
  }, [pendingUsers.dataUpdatedAt, cronHealth.dataUpdatedAt, adapterHealth.dataUpdatedAt,
      fleetPulse.dataUpdatedAt, forecastAccuracy.dataUpdatedAt, automationHealth.dataUpdatedAt,
      telemetryHealth.dataUpdatedAt, jobRuns.dataUpdatedAt, onboardingChecklist.dataUpdatedAt]);

  const handleManualRefresh = useCallback(() => {
    switch (activeTab) {
      case 'users': pendingUsers.refetch(); allUsers.refetch(); break;
      case 'cron': cronHealth.refetch(); break;
      case 'adapters': adapterHealth.refetch(); break;
      case 'fleet': fleetPulse.refetch(); break;
      case 'forecast': forecastAccuracy.refetch(); break;
      case 'automations': automationHealth.refetch(); break;
      case 'telemetry': telemetryHealth.refetch(); break;
      case 'jobs': jobRuns.refetch(); break;
      case 'onboarding': onboardingChecklist.refetch(); break;
    }
  }, [activeTab, pendingUsers, cronHealth, adapterHealth, fleetPulse, forecastAccuracy, automationHealth, telemetryHealth, jobRuns, onboardingChecklist]);

  const isFetching = pendingUsers.isFetching || cronHealth.isFetching || adapterHealth.isFetching ||
                     fleetPulse.isFetching || forecastAccuracy.isFetching || automationHealth.isFetching ||
                     telemetryHealth.isFetching || jobRuns.isFetching || onboardingChecklist.isFetching;

  const formatSecondsAgo = (s: number) => {
    if (s < 60) return `${s} seconds ago`;
    return `${Math.floor(s / 60)} min ago`;
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-wt-text-primary tracking-tight">
            ADMIN OPS CONSOLE
          </h1>
          <p className="text-sm text-wt-text-muted font-mono">
            Last refreshed: {formatSecondsAgo(secondsAgo)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            className="gap-1.5 text-xs"
          >
            {autoRefresh ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isFetching}
            className="gap-1.5 text-xs"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AdminTab)}>
        <TabsList className="bg-wt-bg-secondary border border-wt-border">
          <TabsTrigger value="users" className="text-xs">
            Users
            {(pendingUsers.data?.length ?? 0) > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[1rem] rounded-full bg-wt-amber-primary text-[10px] font-bold text-wt-bg-primary px-1">
                {pendingUsers.data!.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="cron" className="text-xs">Cron Health</TabsTrigger>
          <TabsTrigger value="adapters" className="text-xs">Adapters</TabsTrigger>
          <TabsTrigger value="fleet" className="text-xs">Fleet Pulse</TabsTrigger>
          <TabsTrigger value="forecast" className="text-xs">Forecast</TabsTrigger>
          <TabsTrigger value="automations" className="text-xs">Automations</TabsTrigger>
          <TabsTrigger value="topology" className="text-xs">Topology</TabsTrigger>
          <TabsTrigger value="reports" className="text-xs">Reports</TabsTrigger>
          <TabsTrigger value="telemetry" className="text-xs">Telemetry Health</TabsTrigger>
          <TabsTrigger value="jobs" className="text-xs">Job Runs</TabsTrigger>
          <TabsTrigger value="onboarding" className="text-xs">Onboarding</TabsTrigger>
          <TabsTrigger value="notif_eligibility" className="text-xs">Notif Eligibility</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UserApprovalTab
            data={pendingUsers.data ?? null}
            isLoading={pendingUsers.isLoading}
            error={pendingUsers.error}
            onApproved={() => { pendingUsers.refetch(); allUsers.refetch(); }}
            allUsers={allUsers.data ?? null}
            allUsersLoading={allUsers.isLoading}
          />
        </TabsContent>

        <TabsContent value="cron" className="mt-4">
          <CronHealthTab data={cronHealth.data ?? null} isLoading={cronHealth.isLoading} error={cronHealth.error} />
        </TabsContent>

        <TabsContent value="adapters" className="mt-4">
          <AdapterHealthTab data={adapterHealth.data ?? null} isLoading={adapterHealth.isLoading} error={adapterHealth.error} />
        </TabsContent>

        <TabsContent value="fleet" className="mt-4">
          <FleetPulseTab data={fleetPulse.data ?? null} isLoading={fleetPulse.isLoading} error={fleetPulse.error} />
        </TabsContent>

        <TabsContent value="forecast" className="mt-4">
          <ForecastAccuracyTab
            data={forecastAccuracy.data ?? null}
            isLoading={forecastAccuracy.isLoading}
            error={forecastAccuracy.error}
            lookbackDays={lookbackDays}
            onLookbackChange={setLookbackDays}
            orgId={forecastOrgId}
            onOrgChange={setForecastOrgId}
          />
        </TabsContent>

        <TabsContent value="automations" className="mt-4">
          <AutomationHealthTab data={automationHealth.data ?? null} isLoading={automationHealth.isLoading} error={automationHealth.error} />
        </TabsContent>

        <TabsContent value="topology" className="mt-4">
          <BatteryTopologyTab />
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <ReportReadinessTab />
        </TabsContent>

        <TabsContent value="telemetry" className="mt-4">
          <TelemetryHealthTab data={telemetryHealth.data ?? null} isLoading={telemetryHealth.isLoading} error={telemetryHealth.error} />
        </TabsContent>

        <TabsContent value="jobs" className="mt-4">
          <JobRunsTab data={jobRuns.data ?? null} isLoading={jobRuns.isLoading} error={jobRuns.error} />
        </TabsContent>

        <TabsContent value="notif_eligibility" className="mt-4">
          <NotificationEligibilityTab />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <OnboardingChecklistTab data={onboardingChecklist.data ?? null} isLoading={onboardingChecklist.isLoading} error={onboardingChecklist.error} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
